import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OnboardingShell } from '../../components/onboarding/OnboardingShell';
import { PlaceAutocomplete, type SelectedPlace } from '../../components/onboarding/PlaceAutocomplete';
import { PrettyDateInput } from '../../components/onboarding/PrettyDateInput';
import { TimeOfDayPicker, type TimeOfDay } from '../../components/onboarding/TimeOfDayPicker';
import { useAuth } from '../../store/auth';
import { useMe } from '../../hooks/useMe';
import { useFirstName } from '../../hooks/useFirstName';
import { apiFetch } from '../../lib/api-client';
import {
  TIME_OF_DAY_TO_PRECISION,
  PRECISION_TO_TIME_OF_DAY,
  deriveTimeOfDayFromExact,
} from '../../lib/birth-time';
import styles from './BirthData.module.css';

type TimePrecision = 'EXACT' | 'MORNING' | 'AFTERNOON' | 'EVENING' | 'NIGHT';

export function BirthData(): JSX.Element {
  const { t } = useTranslation('flows');
  const navigate = useNavigate();
  const jwt = useAuth((s) => s.jwt);
  const { data: me } = useMe();
  const firstName = useFirstName();

  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [sex, setSex] = useState<'MALE' | 'FEMALE' | null>(null);
  const [date, setDate] = useState('');
  const [timeOfDay, setTimeOfDay] = useState<TimeOfDay | null>(null);
  const [exactTime, setExactTime] = useState<string | null>(null);
  const [timeUnknown, setTimeUnknown] = useState<boolean>(false);
  const [place, setPlace] = useState<SelectedPlace | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [prefilled, setPrefilled] = useState(false);

  useEffect(() => {
    if (prefilled || !me) return;
    if (me.sex) setSex(me.sex);
    if (me.birthDate) setDate(me.birthDate);
    if (me.birthTimePrecision === 'EXACT' && me.birthTimeExact) {
      setExactTime(me.birthTimeExact);
      setTimeOfDay(deriveTimeOfDayFromExact(me.birthTimeExact));
      setTimeUnknown(false);
    } else if (me.birthTimePrecision && me.birthTimePrecision !== 'EXACT') {
      const tod = PRECISION_TO_TIME_OF_DAY[me.birthTimePrecision as Exclude<TimePrecision, 'EXACT'>];
      if (tod) setTimeOfDay(tod);
      setTimeUnknown(true);
    }
    if (me.birthPlaceName && me.birthLat != null && me.birthLng != null && me.birthTimezone) {
      setPlace({
        placeId: 'prefill',
        placeName: me.birthPlaceName,
        state: null,
        country: null,
        countryCode: null,
        lat: me.birthLat,
        lng: me.birthLng,
        tz: me.birthTimezone,
      });
    }
    setPrefilled(true);
  }, [me, prefilled]);

  const displayName = nameTouched ? name : (name || firstName);
  const nameFromTg = !nameTouched && !name;

  const dateValid = /^\d{4}-\d{2}-\d{2}$/.test(date);
  const exactValid = !timeUnknown && exactTime !== null && /^\d{2}:\d{2}$/.test(exactTime);
  const timeValid = exactValid || (timeUnknown && timeOfDay !== null);
  const placeValid = place !== null;
  const sexValid = sex !== null;
  const canContinue = dateValid && timeValid && placeValid && sexValid && !submitting;

  const handleTimeOfDay = (value: TimeOfDay, _midpoint: string): void => {
    setTimeOfDay(value);
  };

  const handleExactTime = (hhmm: string, tod: TimeOfDay): void => {
    setExactTime(hhmm);
    setTimeOfDay(tod);
  };

  const handleUnknownToggle = (unknown: boolean): void => {
    setTimeUnknown(unknown);
    if (unknown) setExactTime(null);
    else setTimeOfDay(null);
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!canContinue || !jwt || !place) return;
    if (!timeUnknown && !exactTime) return;
    if (timeUnknown && !timeOfDay) return;
    setSubmitting(true);
    setError(null);
    try {
      const body = {
        date,
        sex: sex as 'MALE' | 'FEMALE',
        timePrecision: (timeUnknown
          ? TIME_OF_DAY_TO_PRECISION[timeOfDay as TimeOfDay]
          : 'EXACT') satisfies TimePrecision,
        ...(timeUnknown ? {} : { timeExact: exactTime }),
        placeName: place.placeName,
        lat: place.lat,
        lng: place.lng,
        tz: place.tz,
      };
      const res = await apiFetch('/api/v1/birth-data', jwt, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`save_failed_${res.status}`);
      navigate('/onboarding/computing', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'unknown_error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OnboardingShell step={3} etaSeconds={20} backTo="/onboarding/age">
      <header className={styles.header}>
        <h1 className={styles.title}>{t('onboarding.birthData.title')}</h1>
        <p className={styles.sub}>{t('onboarding.birthData.subtitle')}</p>
      </header>

      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        <label className={styles.field}>
          <span className={styles.label}>{t('onboarding.birthData.name')}</span>
          <div className={styles.nameWrap}>
            <input
              type="text"
              className={styles.input}
              value={displayName}
              onChange={(e) => { setName(e.target.value); setNameTouched(true); }}
              aria-label={t('onboarding.birthData.name')}
              autoComplete="given-name"
            />
            {nameFromTg && (
              <span className={styles.fromTgBadge} aria-label="From Telegram">
                {t('onboarding.birthData.nameBadgeFromTg')}
              </span>
            )}
          </div>
        </label>

        <fieldset className={styles.field}>
          <legend className={styles.label}>{t('onboarding.birthData.sex')}</legend>
          <div className={styles.segmented}>
            <button
              type="button"
              className={`${styles.segmentBtn}${sex === 'MALE' ? ` ${styles.segmentBtnActive}` : ''}`}
              onClick={() => setSex('MALE')}
              aria-pressed={sex === 'MALE'}
            >
              {t('onboarding.birthData.male')}
            </button>
            <button
              type="button"
              className={`${styles.segmentBtn}${sex === 'FEMALE' ? ` ${styles.segmentBtnActive}` : ''}`}
              onClick={() => setSex('FEMALE')}
              aria-pressed={sex === 'FEMALE'}
            >
              {t('onboarding.birthData.female')}
            </button>
          </div>
        </fieldset>

        <label className={styles.field}>
          <span className={styles.label}>{t('onboarding.birthData.birthDate')}</span>
          <PrettyDateInput
            value={date}
            onChange={setDate}
            ariaLabel={t('onboarding.birthData.birthDate')}
          />
        </label>

        <fieldset className={styles.field}>
          <legend className={styles.label}>{t('onboarding.birthData.birthTime')}</legend>
          <TimeOfDayPicker
            value={timeOfDay}
            exactTime={exactTime}
            unknownMode={timeUnknown}
            onChange={handleTimeOfDay}
            onExactTimeChange={handleExactTime}
            onUnknownToggle={handleUnknownToggle}
          />
        </fieldset>

        <label className={styles.field}>
          <span className={styles.label}>{t('onboarding.birthData.birthPlace')}</span>
          <PlaceAutocomplete
            value={place}
            onChange={setPlace}
            placeholder={t('onboarding.birthData.birthPlace')}
            ariaLabel={t('onboarding.birthData.birthPlace')}
          />
        </label>

        <p className={styles.info}>{t('onboarding.birthData.infoBlock')}</p>

        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          className={styles.cta}
          disabled={!canContinue}
          aria-disabled={!canContinue}
        >
          {submitting ? t('onboarding.birthData.saving') : t('onboarding.birthData.cta')}
        </button>
      </form>
    </OnboardingShell>
  );
}
